import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Leaf, Mail, Lock, LogIn } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";

export default function Login() {
    const { t } = useT();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { login, error, setError } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const from = loc.state?.from?.pathname || "/";

    async function onSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        const ok = await login(email.trim(), password);
        setSubmitting(false);
        if (ok) {
            toast.success(t("login.toast_success"));
            nav(from, { replace: true });
        }
    }

    return (
        <div className="min-h-screen bg-bone grid place-items-center px-4 py-10">
            <div className="w-full max-w-md">
                <div className="flex justify-end mb-4"><LanguageSwitcher /></div>
                <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="auth-logo">
                    <div className="w-14 h-14 rounded-2xl bg-forest text-white grid place-items-center shadow-md">
                        <Leaf size={28} />
                    </div>
                    <div>
                        <div className="font-display text-2xl text-forest-dark leading-tight">HACCP Manager</div>
                        <div className="text-xs text-stone-500 -mt-0.5">{t("brand.tagline")}</div>
                    </div>
                </Link>

                <div className="bg-white rounded-2xl shadow-sm border border-soft p-7" data-testid="login-card">
                    <h1 className="font-display text-2xl text-forest-dark mb-1">{t("login.title")}</h1>
                    <p className="text-sm text-stone-500 mb-6">{t("login.subtitle")}</p>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <label className="block">
                            <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">{t("auth.email")}</span>
                            <div className="mt-1 relative" dir="ltr">
                                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                <input
                                    type="email"
                                    required
                                    dir="ltr"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setError(null);
                                    }}
                                    className="input pl-10 text-left"
                                    style={{ textAlign: "left" }}
                                    placeholder={t("login.email_placeholder")}
                                    data-testid="login-email"
                                />
                            </div>
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">{t("auth.password")}</span>
                            <div className="mt-1">
                                <PasswordInput
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setError(null);
                                    }}
                                    className="input"
                                    placeholder="••••••••"
                                    data-testid="login-password"
                                    iconLeft={<Lock size={16} />}
                                />
                            </div>
                        </label>

                        {error && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary w-full inline-flex items-center justify-center gap-2"
                            data-testid="login-submit"
                        >
                            <LogIn size={16} />
                            {submitting ? t("login.submitting") : t("login.submit")}
                        </button>
                    </form>

                    <div className="flex items-center justify-between mt-5 text-sm">
                        <Link to="/forgot-password" className="text-forest hover:underline" data-testid="login-forgot-link">
                            {t("auth.forgot_password")}
                        </Link>
                        <Link to="/register" className="text-forest hover:underline font-medium" data-testid="login-register-link">
                            {t("auth.create_account")}
                        </Link>
                    </div>
                </div>

                <p className="text-center text-xs text-stone-500 mt-6">
                    {t("login.footer_note")}
                </p>
            </div>
        </div>
    );
}
